 
import { IServerSideGetRowsParams } from 'ag-grid-community'
import { useAppDispatch, useAppSelector } from 'appState/store'
import { ObGrid } from '@outbound-ai/ursa'
import {
  CLAIMS_FILE_COLUMN,
  ROW_MODEL_TYPE,
} from 'features/claims/components/ClaimsTable/Internal/ClaimsTableConstants'
import { useEffect, useMemo, useState } from 'react'
import { FaTimes } from 'react-icons/fa'
import { Button } from '@outbound-ai/ursa'
import styled, { css } from 'styled-components'
import { notificator } from 'utils/notificator/notificator'
import { archiveClaims } from '../autoCheckApi'
import {
  updateCurrentBatch,
  updateProcessedFilters,
  updateProcessedGridApi,
  updateProcessedSort,
} from '../autoCheckSlice'
import { BatchTabType } from '../types'
import { NoContent } from '../utils'
import { createBatchServerSideDataSource } from '../utils/grid'
import { BatchGridProcessdConfig } from './AutoCheckGrid/gridConfig'
import { settingsServiceApiPaths, setUserPreference } from 'features/settings/settingsApi'
import { UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import { useTenantUserPreference } from 'features/settings/settingsHooks'

export const BatchProcessed = () => {
  const dispatch = useAppDispatch()

  const { root, refresh } = useAppSelector((state) => state.batch)
  const [gridApi, setGridApi] = useState<any>(null)
  const [isselectedRows, setSelectedRows] = useState<any>([])

  const { data: filterPreference, mutate: mutateFiltersPreference } = useTenantUserPreference(
    UserPreferencesKeysEnum.batchProcessedFilters,
  )

  const { data: sortPreference, mutate: mutateSortPreference } = useTenantUserPreference(
    UserPreferencesKeysEnum.batchProcessedSort,
  )

  const filterString = filterPreference?.value
  const sortString = sortPreference?.value

  const onGridReady = async (params: IServerSideGetRowsParams) => {
    setGridApi(params)
    dispatch(updateProcessedGridApi(params))

    dispatch(updateCurrentBatch({ root: BatchTabType.Processed }))

    const dataSource = createBatchServerSideDataSource(BatchTabType.Processed)
    params.api.setGridOption('serverSideDatasource', dataSource)
  }

  // Load filters from preferences when filterString is available and gridApi is ready
  useEffect(() => {
    if (filterString && gridApi?.api) {
      try {
        const parsedFilters = JSON.parse(filterString)
        dispatch(updateProcessedFilters({ filters: parsedFilters }))
        // Apply filters to grid after a short delay to ensure datasource is ready
        setTimeout(() => {
          if (gridApi?.api) {
            gridApi.api.setFilterModel(parsedFilters)
          }
        }, 200)
      } catch (error) {
        console.error('Error loading filter preferences:', error)
      }
    }
  }, [filterString, gridApi, dispatch])

  // Load sort from preferences when sortString is available and gridApi is ready
  useEffect(() => {
    if (sortString && gridApi?.columnApi) {
      try {
        const parsedSort = JSON.parse(sortString)
        dispatch(updateProcessedSort({ sort: parsedSort }))
        // Apply sort to grid after a short delay to ensure datasource is ready
        setTimeout(() => {
          if (gridApi?.columnApi) {
            gridApi.api.applyColumnState({
              state: parsedSort,
              defaultState: { sort: null },
            })
          }
        }, 200)
      } catch (error) {
        console.error('Error loading sort preferences:', error)
      }
    }
  }, [sortString, gridApi, dispatch])

  const getRowId = useMemo(() => {
    return (params: any) => {
      const data = params.data
      return data.batchCode + data.batchId
    }
  }, [])

  const onFirstDataRendered = (params: any) => {
    params.api.sizeColumnsToFit()
    if (window.innerWidth > 1600) {
      params.api.sizeColumnsToFit()
    } else {
      window.setTimeout(() => {
        const colIds = params.api.getColumns().map((col: any) => col.getColId())
        params.api.autoSizeColumns(colIds)
      })
    }
  }

  const onSelectionChanged = (api: any) => {
    const selectedRows = api.getSelectedRows()
    setSelectedRows(selectedRows)
  }

  const onFilterChanged = async () => {
    if (!gridApi?.api) return

    // Deselect rows when filter changes
    if (isselectedRows.length > 0) {
      gridApi.api.deselectAll()
    }

    const filterModel = gridApi.api.getFilterModel() || {}

    // Update Redux with current filters
    dispatch(updateProcessedFilters({ filters: filterModel }))

    // Save to user preferences
    if (JSON.stringify(filterModel) !== filterString) {
      const newFilters = {
        key: UserPreferencesKeysEnum.batchProcessedFilters,
        value: JSON.stringify(filterModel),
      }
      await setUserPreference(settingsServiceApiPaths.tenantUserPreferencesPut(), newFilters)
      await mutateFiltersPreference(newFilters, false)
    }

    // Refresh grid based on new filters
    gridApi.api.refreshServerSide({ purge: true })
  }

  const deselectClaims = () => {
    gridApi.api.deselectAll()
    setSelectedRows([])
    const x = document.querySelector('#selectGridHeader') as HTMLInputElement
    x.checked = false
  }

  const onArchive = () => {
    const batchArchive = {
      batches: isselectedRows.map((item: any) => {
        return {
          batchId: item.batchId,
          archived: true,
        }
      }),
    }
    archiveClaims(batchArchive)
      .then((res) => {
        if (res) {
          dispatch(updateCurrentBatch({ root: BatchTabType.Processed, refresh: true }))
          setSelectedRows([])
          deselectClaims()
          notificator.notify({
            type: 'success',
            heading: 'Batch archived successfully',
          })
        }
      })
      .catch((err) => {
        notificator.notify({
          type: 'error',
          heading: err.response?.data?.detail || err.message,
        })
      })
  }

  useEffect(() => {
    if (refresh && gridApi) {
      const dataSource = createBatchServerSideDataSource(root)
      gridApi.api.setGridOption('serverSideDatasource', dataSource)
      dispatch(updateCurrentBatch({ root: BatchTabType.Processed, refresh: false }))
    }
  }, [refresh, root, gridApi, dispatch])

  return (
    <Div>
      {isselectedRows.length > 0 && (
        <div className="action-holder">
          <div className="actions">
            <span>{isselectedRows.length} batch selected </span>{' '}
            <Button variant="primary-outline" radius="rounded" size="small" onClick={onArchive}>
              Archive
            </Button>
          </div>
          <div>
            <Button variant="tertiary" onClick={deselectClaims}>
              <FaTimes /> Deselect & Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="grid">
        <ObGrid
          reactiveCustomComponents={true}
          columnDefs={BatchGridProcessdConfig as any}
          rowModelType={ROW_MODEL_TYPE}
          onGridReady={onGridReady as any}
          defaultColDef={CLAIMS_FILE_COLUMN as any}
          rowSelection="multiple"
          getRowId={getRowId}
          suppressRowClickSelection={true}
          onSelectionChanged={() => {
            onSelectionChanged(gridApi.api)
          }}
          onPaginationChanged={() => {
            if (isselectedRows.length > 0) {
              gridApi.api.deselectAll()
            }
          }}
          onFilterChanged={onFilterChanged}
          onSortChanged={async () => {
            if (isselectedRows.length > 0) {
              gridApi.api.deselectAll()
            }

            if (!gridApi?.columnApi) return

            // Get current sort state
            const sortModel = gridApi.api
              .getColumnState()
              .filter((col: any) => col.sort != null)
              .map((col: any) => ({
                colId: col.colId,
                sort: col.sort,
                sortIndex: col.sortIndex,
              }))

            // Update Redux with current sort
            dispatch(updateProcessedSort({ sort: sortModel }))

            // Save to user preferences
            if (JSON.stringify(sortModel) !== sortString) {
              const newSort = {
                key: UserPreferencesKeysEnum.batchProcessedSort,
                value: JSON.stringify(sortModel),
              }
              await setUserPreference(settingsServiceApiPaths.tenantUserPreferencesPut(), newSort)
              await mutateSortPreference(newSort, false)
            }
          }}
          rowHeight={50}
          onFirstDataRendered={onFirstDataRendered}
          paginationPageSize={20}
          cacheBlockSize={20}
          noRowsOverlayComponent={NoContent}
        ></ObGrid>
      </div>
    </Div>
  )
}

const Div = styled.div`
  padding: 1.5rem 1rem 0rem 0rem;
  overflow: hidden;
  .grid {
    ${({ theme }) => css`
      height: calc(
        100vh - ${theme.constants.navbarHeight} - ${theme.constants.pageTitleHeight} -
          ${theme.constants.tableHeadHeight} - 90px
      );
    `}
  }
  h2 {
    font-family: ${({ theme }) => theme.fontFamily.milliard};
    ${({ theme }) => theme.typography.h5.bold};
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.primary.darker};
  }
  .action-holder {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;
    .actions {
      display: flex;
      gap: 1rem;
      align-items: center;

      span {
        ${({ theme }) => theme.typography.m.medium};
        color: ${({ theme }) => theme.colors.neutral.neutral};
      }
    }
    width: 81%;
    position: absolute;
    z-index: 101;
    top: 92px;
    padding: ${({ theme }) => theme.spacing.xs};
    border-radius: 8px;
    box-shadow: 0 8px 14px 0 rgba(0, 0, 0, 0.06);
    border: solid 1px ${({ theme }) => theme.colors.grey.lighter};
    background-color: ${({ theme }) => theme.colors.neutral.lightest};
  }
`
